import { supabase } from './supabase';
import { logService } from './logService';
import type { UserProfile } from '../types/forms';

// =====================================================
// FUNÇÕES DE AUTENTICAÇÃO - ONEFORM
// =====================================================

/**
 * Registrar novo usuário
 */
export async function signUp(
  email: string,
  password: string,
  fullName?: string
): Promise<{ user: UserProfile | null; error: string | null }> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/login?confirmed=true`,
      },
    });

    if (error) {
      // Supabase explicit already-registered error (quando duplicate detection
      // está habilitado nas settings do projeto).
      const msg = error.message?.toLowerCase() ?? '';
      if (
        msg.includes('already registered') ||
        msg.includes('already exists') ||
        msg.includes('user_already_exists') ||
        (error as { status?: number }).status === 422
      ) {
        return {
          user: null,
          error: 'Este email já está cadastrado. Tente fazer login ou recuperar a senha.',
        };
      }
      return { user: null, error: error.message };
    }

    if (!data.user) {
      return { user: null, error: 'Erro ao criar usuário' };
    }

    // Supabase Auth default (com "obscure email in signup" ligado) retorna
    // 200 com data.user populado MAS data.user.identities = [] quando o
    // email já existe. Detecção heurística — esse é o behavior documentado
    // pra não vazar se o email existe, mas na nossa UX preferimos informar.
    if (Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      return {
        user: null,
        error: 'Este email já está cadastrado. Tente fazer login ou recuperar a senha.',
      };
    }

    // Não buscar profile aqui — o usuário pode não ter sessão ativa
    // (email confirmation pendente), então RLS bloquearia a query.
    return { user: null, error: null };
  } catch (err) {
    logService.error('auth.signUp', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { user: null, error: 'Erro inesperado ao criar conta' };
  }
}

/**
 * Login com email e senha
 */
export async function signIn(
  email: string,
  password: string
): Promise<{ user: UserProfile | null; error: string | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      logService.warn('auth.signIn', error.message, { supabase_code: error.code });
      // Traduzir mensagens comuns de erro
      if (error.message.includes('Invalid login credentials')) {
        return { user: null, error: 'Email ou senha incorretos' };
      }
      if (error.message.includes('Email not confirmed')) {
        return { user: null, error: 'Confirme seu email antes de fazer login' };
      }
      return { user: null, error: error.message };
    }

    if (!data.user) {
      return { user: null, error: 'Erro ao fazer login' };
    }

    // Buscar profile — com fallback para dados do auth.user
    const profile = await getProfile(data.user.id);

    if (profile) {
      return { user: profile, error: null };
    }

    // Fallback: construir profile mínimo a partir dos dados do auth
    const fallbackProfile: UserProfile = {
      id: data.user.id,
      email: data.user.email || email,
      full_name: data.user.user_metadata?.full_name || '',
      avatar_url: data.user.user_metadata?.avatar_url || '',
      role: 'collaborator',
      created_at: new Date().toISOString(),
    };

    return { user: fallbackProfile, error: null };
  } catch (err) {
    logService.error('auth.signIn', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { user: null, error: 'Erro inesperado ao fazer login' };
  }
}

/**
 * Logout
 */
export async function signOut(): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  } catch (err) {
    logService.error('auth.signOut', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { error: 'Erro inesperado ao sair' };
  }
}

/**
 * Enviar email de recuperação de senha
 */
export async function sendPasswordReset(
  email: string
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  } catch (err) {
    logService.error('auth.resetPassword', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { error: 'Erro inesperado ao enviar email' };
  }
}

/**
 * Atualizar senha (após reset)
 */
export async function updatePassword(
  newPassword: string
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  } catch (err) {
    logService.error('auth.updatePassword', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { error: 'Erro inesperado ao atualizar senha' };
  }
}

/**
 * Atualizar email do usuário
 */
export async function updateEmail(
  newEmail: string
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      email: newEmail,
    });

    if (error) {
      if (error.message.includes('email_exists')) {
        return { error: 'Este email já está em uso' };
      }
      return { error: error.message };
    }

    return { error: null };
  } catch (err) {
    logService.error('auth.updateEmail', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { error: 'Erro inesperado ao atualizar email' };
  }
}

/**
 * Buscar perfil do usuário
 */
export async function getProfile(userId: string): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      logService.error('auth.getProfile', error.message, { supabase_code: error.code });
      return null;
    }

    return data as UserProfile;
  } catch (err) {
    logService.error('auth.getProfile', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return null;
  }
}

/**
 * Atualizar perfil do usuário
 */
export async function checkUsernameAvailability(
  username: string,
  excludeUserId?: string
): Promise<{ available: boolean; error: string | null }> {
  try {
    let query = supabase
      .from('profiles')
      .select('id')
      .eq('username', username.toLowerCase());

    if (excludeUserId) {
      query = query.neq('id', excludeUserId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) return { available: false, error: error.message };
    return { available: data === null, error: null };
  } catch (err) {
    logService.error('auth.checkUsername', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { available: false, error: 'Erro ao verificar username' };
  }
}

export async function updateProfile(
  userId: string,
  updates: Partial<Pick<UserProfile, 'full_name' | 'avatar_url' | 'username'>>
): Promise<{ profile: UserProfile | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      return { profile: null, error: error.message };
    }

    return { profile: data as UserProfile, error: null };
  } catch (err) {
    logService.error('auth.updateProfile', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return { profile: null, error: 'Erro inesperado ao atualizar perfil' };
  }
}

/**
 * Verificar sessão atual
 */
export async function getCurrentSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      logService.error('auth.getSession', error.message, { supabase_code: error.code });
      return null;
    }

    return session;
  } catch (err) {
    logService.error('auth.getSession', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return null;
  }
}

/**
 * Verificar se usuário está autenticado
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getCurrentSession();
  return session !== null;
}

/**
 * Obter usuário atual
 */
export async function getCurrentUser(): Promise<UserProfile | null> {
  try {
    const session = await getCurrentSession();

    if (!session?.user) {
      return null;
    }

    const profile = await getProfile(session.user.id);

    if (profile) {
      return profile;
    }

    // Fallback: construir profile a partir dos dados da sessão
    return {
      id: session.user.id,
      email: session.user.email || '',
      full_name: session.user.user_metadata?.full_name || '',
      avatar_url: session.user.user_metadata?.avatar_url || '',
      role: 'collaborator',
      created_at: new Date().toISOString(),
    };
  } catch (err) {
    logService.error('auth.getCurrentUser', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    return null;
  }
}

/**
 * Listener para mudanças de autenticação
 *
 * IMPORTANTE: o auth-js segura um Navigator Lock (compartilhado entre abas)
 * enquanto aguarda os callbacks deste listener. Qualquer query Supabase
 * aguardada aqui dentro precisa do mesmo lock (via getSession) → deadlock
 * que congela todas as queries do app até recarregar todas as abas.
 * Por isso o callback retorna síncrono e o trabalho roda em setTimeout(0),
 * fora do lock.
 */
export function onAuthStateChange(
  callback: (user: UserProfile | null) => void
) {
  let lastUserId: string | null = null;
  let seq = 0;

  return supabase.auth.onAuthStateChange((event, session) => {
    // Ignorar eventos que não precisam atualizar o perfil no React state
    if (event === 'TOKEN_REFRESHED' || event === 'PASSWORD_RECOVERY') {
      return;
    }

    // SIGNED_IN re-dispara a cada refocus de aba — perfil já carregado
    if (event === 'SIGNED_IN' && session?.user && session.user.id === lastUserId) {
      return;
    }

    const current = ++seq;
    setTimeout(async () => {
      if (!session?.user) {
        lastUserId = null;
        if (current === seq) callback(null);
        return;
      }

      const profile = await getProfile(session.user.id);
      if (current !== seq) return; // evento mais novo chegou durante o fetch

      lastUserId = session.user.id;
      if (profile) {
        callback(profile);
      } else {
        // Fallback: construir profile a partir dos dados da sessão
        callback({
          id: session.user.id,
          email: session.user.email || '',
          full_name: session.user.user_metadata?.full_name || '',
          avatar_url: session.user.user_metadata?.avatar_url || '',
          role: 'collaborator',
          created_at: new Date().toISOString(),
        });
      }
    }, 0);
  });
}
